import { useNavigate, useParams } from "react-router-dom";
import { ActionRail } from "@/components/ActionRail";
import { Bread } from "@/components/Bread";
import { DayLoader } from "@/components/DayLoader";
import { WordRow } from "@/components/WordRow";
import { STATUS_DOT, STATUS_LABEL } from "@/components/status";
import { useIssueViews } from "@/hooks/useIssueViews";
import { useDayMastery } from "@/hooks/useMastery";
import { pad } from "@/lib/format";
import type { DayData, IssueView } from "@/types/lex";

const LEARNING_MODES = [
  { id: "flashcard", num: "M01", label: "Flashcards", hint: "Flip · 3–10 min" },
  { id: "quiz",      num: "M02", label: "Quiz",       hint: "Multiple choice" },
  { id: "typing",    num: "M03", label: "Typing",     hint: "Hear · spell" },
  { id: "fillblank", num: "M04", label: "Cloze",      hint: "Fill in context" },
] as const;

export function DayDetail() {
  const { day: dayParam } = useParams<{ day: string }>();
  const day = Number(dayParam) || 1;
  const issues = useIssueViews();
  const issue = issues.find((i) => i.day === day) ?? issues[0];

  return (
    <DayLoader day={day}>
      {(data) => <Body day={day} data={data} issue={issue} />}
    </DayLoader>
  );
}

interface BodyProps {
  day: number;
  data: DayData;
  issue: IssueView;
}

function Body({ day, data, issue }: BodyProps) {
  const navigate = useNavigate();
  const dayMastery = useDayMastery(day);

  return (
    <div className="page">
      <div className="container" style={{ paddingTop: "var(--s-5)" }}>
        <Bread
          crumbs={[
            { label: "Archive", to: "/" },
            { label: `Day ${pad(issue.day)}` },
            { label: "Overview" },
          ]}
        />
      </div>

      <section className="container" style={{ paddingTop: "var(--s-5)" }}>
        <div className="day-hero">
          <div className="row-s" style={{ marginBottom: "var(--s-5)" }}>
            <div className="t-mono" style={{ fontSize: 12, color: "var(--text-muted)", letterSpacing: "0.1em" }}>
              DAY {pad(issue.day)} · {data.words.length} ENTRIES
            </div>
            <div className="row-c" style={{ gap: 8 }}>
              <span className={`dot dot--${STATUS_DOT[issue.status]}`} />
              <span className="t-meta" style={{ fontSize: 11 }}>{STATUS_LABEL[issue.status]}</span>
            </div>
          </div>
          <h1 className="day-hero__theme">{data.theme_en}</h1>
          <div className="day-hero__theme-vi">{data.theme_vi}</div>
          <p className="day-hero__intro">{data.intro}</p>
          <div className="day-hero__nr">{pad(issue.day)}</div>
        </div>
      </section>

      <section className="container" style={{ marginBottom: "var(--s-7)" }}>
        <ActionRail
          items={LEARNING_MODES}
          onPick={(id) => navigate(`/${id}/${day}`)}
        />
      </section>

      <section className="container">
        <div className="row-s" style={{ marginBottom: "var(--s-4)" }}>
          <div className="row-c" style={{ gap: "var(--s-4)" }}>
            <h2 className="t-display" style={{ fontSize: "var(--t-xl)", margin: 0 }}>Entries</h2>
            <div className="t-mono" style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {data.words.length} words
            </div>
          </div>
          <div className="row-c" style={{ gap: 16 }}>
            <MasteryCount dot="moss" label="known" value={dayMastery.counts.known} />
            <MasteryCount dot="amber" label="learning" value={dayMastery.counts.learning} />
            <MasteryCount dot="red" label="unknown" value={dayMastery.counts.unknown} />
          </div>
        </div>
        <div className="word-list" style={{ borderTop: "var(--border-thin)" }}>
          {data.words.map((w) => (
            <WordRow
              key={w.index}
              word={w}
              mastery={dayMastery.byIndex(w.index)}
              onOpen={(idx) => navigate(`/word/${day}/${idx}`)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function MasteryCount({ dot, label, value }: { dot: "moss" | "amber" | "red"; label: string; value: number }) {
  return (
    <span className="t-meta" style={{ fontSize: 11 }}>
      <span className={`dot dot--${dot}`} style={{ marginRight: 6 }} /> {label} {value}
    </span>
  );
}
